import { useCallback, useEffect, useState } from 'react';
import { datasetsApi, inversionApi } from '../lib/api';
import { productToModel } from '../lib/inversion-parser';
import { recordToEntry } from '../lib/inversion-results';

const detail = (err) => err?.response?.data?.detail || err?.message || 'request failed';

/** The project to store with: `?project=`, else the project of `?dataset=` (the dataset
 *  page's "Show on map" link carries only the dataset). */
function useProjectOf(projectParam, datasetId) {
  const [fromDataset, setFromDataset] = useState(null);
  useEffect(() => {
    if (projectParam || !datasetId) { setFromDataset(null); return undefined; }
    let live = true;
    datasetsApi.get(datasetId)
      .then((res) => { if (live) setFromDataset(res.data?.project_id || null); })
      .catch(() => { if (live) setFromDataset(null); });
    return () => { live = false; };
  }, [projectParam, datasetId]);
  return projectParam || fromDataset;
}

/**
 * Stored inversion models of a project (V.12, docs/GEOREFERENCING_AND_MODEL_IMPORT.md
 * "Import persistence"). The map's Inversion panel parses a file for display and then
 * stores it with POST /api/inversion/import, so the model survives a reload and reaches
 * the L-curve tab, the server time-lapse difference, /3d and the dataset page.
 *
 *   projectId  - project the models belong to (?project=; else the ?dataset= project; none: nothing is stored)
 *   datasetId  - optional dataset the panel was opened from (?dataset=); linked on import
 *
 * Returns { projectId, items, loading, error, refresh, store, load, remove }:
 *   store({ name, text, fileName, iteration, projectId? }) -> (projectId: store with another
 *     project than the map's, e.g. the one picked in the Save dialog)
 *     { status: 'stored', id, meta, projectId } | { status: 'queued', jobId, message } | { status: 'local', message }
 *   load(id)   -> a panel model entry (lib/inversion-results recordToEntry)
 *   remove(id) -> DELETE /api/inversion/{id}, then refresh
 */
export default function useStoredInversions({ projectId: projectParam, datasetId = null }) {
  const projectId = useProjectOf(projectParam, datasetId);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!projectId) { setItems([]); return []; }
    setLoading(true);
    try {
      const res = await inversionApi.list({ project_id: projectId });
      const list = res.data || [];
      setItems(list);
      setError(null);
      return list;
    } catch (err) {
      setError(`Could not load stored models: ${detail(err)}`);
      return [];
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  const store = useCallback(async ({ name, text, fileName, iteration = null, projectId: override = null }) => {
    const target = override || projectId;
    if (!target) {
      return { status: 'local', message: 'Not saved: open the map from a project to store imported models. This model lives in this browser tab only.' };
    }
    try {
      const res = await inversionApi.import({
        name, content: text, file_name: fileName, iteration,
        project_id: target, ...(datasetId && target === projectId ? { dataset_id: datasetId } : {}),
      });
      // Files over 2 MB go to a background job when the queue runs (backend jobs/thresholds.py)
      if (res.status === 202) {
        return {
          status: 'queued', jobId: res.data?.job_id,
          message: `Large file (${(text.length / 1048576).toFixed(1)} MB): it is being stored by a background import job and appears under Stored models when the job finishes. The copy shown now lives in this browser tab only.`,
        };
      }
      const { id, ...meta } = res.data || {};
      if (target === projectId) refresh();
      return { status: 'stored', id, meta, projectId: target };
    } catch (err) {
      return { status: 'local', message: `Not saved (${detail(err)}). This model lives in this browser tab only.` };
    }
  }, [projectId, datasetId, refresh]);

  const load = useCallback(async (id) => {
    const res = await inversionApi.get(id);
    return recordToEntry(res.data, productToModel);
  }, []);

  const remove = useCallback(async (id) => {
    await inversionApi.remove(id);
    await refresh();
  }, [refresh]);

  return { projectId, items, loading, error, refresh, store, load, remove };
}
